import { useEffect } from "react";
import { Form, Input, DatePicker, Skeleton, Select } from "../../components/ui";
import { useAddFee, useGetFeeById, useUpdateFee } from "../../query/feesQuery";
import { useGetAllChildren } from "../../query/childQuery";
import swal from "../../utils/swal";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { MONTH_NAMES, canEditVoucher } from "./feeHelpers";
import "../teachers/addTeacher.css";
import "./fees.css";

function asDateString(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value.format) return value.format("YYYY-MM-DD");
  return "";
}

function childOptionLabel(child) {
  const name = `${child.firstName || ""} ${child.lastName || ""}`.trim();
  return name || child.rollNumber || "Student";
}

function AddFee() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [form] = Form.useForm();
  const { mutate: addFee, isPending: isAdding } = useAddFee();
  const { mutate: updateFee, isPending: isUpdating } = useUpdateFee();
  const { data: fee, isLoading } = useGetFeeById(isEdit ? id : undefined);
  const { data: childrenData, isLoading: loadingChildren } = useGetAllChildren({ page: 1, limit: 200 });
  const children = childrenData?.docs || [];
  const isMutating = isAdding || isUpdating;
  const locked = isEdit && fee && !canEditVoucher(fee);

  const monthOptions = MONTH_NAMES.map((label, index) => ({
    value: String(index + 1),
    label,
  }));

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = currentYear - 1 + i;
    return { value: String(year), label: String(year) };
  });

  useEffect(() => {
    if (!isEdit || !fee) return;
    form.setFieldsValue({
      children: fee.children?._id || fee.children || "",
      amount: fee.baseAmount ?? fee.amount ?? "",
      discountAmount: fee.discountAmount ?? "",
      month: fee.month ? String(fee.month) : "",
      year: fee.year ? String(fee.year) : "",
      dueDate: fee.dueDate ? dayjs(fee.dueDate) : null,
      issuedAt: fee.issuedAt ? dayjs(fee.issuedAt) : null,
    });
  }, [isEdit, fee, form]);

  const onFinish = (values) => {
    if (locked) return;
    const payload = {
      children: values.children,
      amount: Number(values.amount),
      discountAmount: values.discountAmount === "" || values.discountAmount == null ? 0 : Number(values.discountAmount),
      month: Number(values.month),
      year: Number(values.year),
      dueDate: asDateString(values.dueDate),
      issuedAt: asDateString(values.issuedAt),
    };

    if (isEdit) {
      updateFee(
        { id, ...payload },
        {
          onSuccess: () => {
            swal("Success", "Fee voucher updated successfully", "success");
            navigate(`/fees/${id}`);
          },
          onError: (error) => swal("Error", error?.message || "Failed to update voucher", "error"),
        }
      );
      return;
    }

    addFee(payload, {
      onSuccess: () => {
        swal("Success", "Fee voucher created successfully", "success");
        navigate("/fees");
      },
      onError: (error) => swal("Error", error?.message || "Failed to create voucher", "error"),
    });
  };

  if (isEdit && isLoading) {
    return (
      <div className="tf-page">
        <Skeleton active />
      </div>
    );
  }

  return (
    <div className="tf-page">
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <section className="tf-card">
          <h2 className="tf-card-head">{isEdit ? "Edit Fee Voucher" : "New Fee Voucher"}</h2>
          <div className="tf-card-body">
            {locked ? (
              <p className="fee-send-lead">This voucher is closed and can no longer be edited.</p>
            ) : null}
            <div className="tf-field">
              <p className="tf-label">
                Student <span>*</span>
              </p>
              <Form.Item
                name="children"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select a student" }]}
              >
                <Select
                  placeholder="Select student"
                  loading={loadingChildren}
                  disabled={isEdit}
                  options={children.map((child) => ({
                    value: child._id,
                    label: childOptionLabel(child),
                  }))}
                  onChange={(value) => {
                    if (isEdit) return;
                    const child = children.find((item) => item._id === value);
                    if (!child) return;
                    form.setFieldsValue({
                      amount: child.fee?.monthlyAmount || "",
                      discountAmount: child.fee?.discountAmount || "",
                    });
                  }}
                />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Amount <span>*</span>
              </p>
              <Form.Item
                name="amount"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please enter amount" }]}
              >
                <Input type="number" min={0} placeholder="5000" disabled={locked} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Discount (PKR)</p>
              <Form.Item name="discountAmount" className="tf-item mb-0">
                <Input type="number" min={0} placeholder="0" disabled={locked} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Month <span>*</span>
              </p>
              <Form.Item
                name="month"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select month" }]}
              >
                <Select placeholder="Select month" options={monthOptions} disabled={locked} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Year <span>*</span>
              </p>
              <Form.Item
                name="year"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select year" }]}
              >
                <Select placeholder="Select year" options={yearOptions} disabled={locked} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">Send date</p>
              <Form.Item name="issuedAt" className="tf-item mb-0">
                <DatePicker placeholder="Today" disabled={locked} />
              </Form.Item>
            </div>

            <div className="tf-field">
              <p className="tf-label">
                Due Date <span>*</span>
              </p>
              <Form.Item
                name="dueDate"
                className="tf-item mb-0"
                rules={[{ required: true, message: "Please select due date" }]}
              >
                <DatePicker placeholder="Select due date" disabled={locked} />
              </Form.Item>
            </div>
          </div>
        </section>

        <div className="tf-actions">
          <button type="button" className="tf-btn is-ghost" onClick={() => navigate("/fees")}>
            Cancel
          </button>
          <button type="submit" className="tf-btn is-primary" disabled={isMutating || locked}>
            {isMutating ? "Saving..." : isEdit ? "Save" : "Create Voucher"}
          </button>
        </div>
      </Form>
    </div>
  );
}

export default AddFee;
